<template>
  <div class="stack-trace-viewer">
    <div class="stack-trace-header" :title="stackTraceItem.threadName">
      <div>Stack trace {{ stackTraceIndex + 1 }}/{{ stackTracesCount }}</div>
      <div class="thread-name">Thread Name: {{ stackTraceItem.threadName }}</div>
    </div>

    <div class="stack-trace-frames">
      <div class="line" :class="colorOfVulnerabilityForLine(severity)"></div>
      <div class="stack-trace-frames-inner">
        <div
          v-for="(frame, frameIndex) in safeFrames"
          :key="`frame_${frameIndex}`"
          class="frame-item"
        >
          <div
            class="frame-header"
            :title="getFrameTitle(frame)"
            :class="{ highlighted: isFrameHighlighted(frameIndex) }"
            @click="toggleFrame(frame, frameIndex)"
          >
            <span class="frame-number">{{ frameIndex + 1 }}</span>

            <div
              v-if="hasVariables(frame)"
              class="arrow-container"
              :class="{ expanded: expandedFrames[frameIndex] }"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M17.7364 9.3636C17.3849 9.01213 16.8151 9.01213 16.4636 9.36361L12 13.8272L7.53639 9.3636C7.18492 9.01213 6.61507 9.01213 6.2636 9.36361C5.91213 9.71508 5.91213 10.2849 6.2636 10.6364L11.3636 15.7364C11.7151 16.0879 12.285 16.0879 12.6364 15.7364L17.7364 10.6364C18.0879 10.2849 18.0879 9.71507 17.7364 9.3636Z"
                  fill="#949DB8"
                />
              </svg>
            </div>

            <span v-else class="expand-placeholder"></span>

            <span>
              {{ getFrameDisplayInfo(frame) }}
            </span>
          </div>

          <div v-if="expandedFrames[frameIndex] && hasVariables(frame)" class="frame-variables">
            <div
              v-for="(variable, varIndex) in getFrameVariables(frame)"
              :key="`var_${frameIndex}_${varIndex}`"
              class="variable-item"
            >
              <div
                class="variable-content"
                :class="{
                  highlighted: isVariableHighlighted(frameIndex, variable.name),
                }"
                @click="toggleVariable(frameIndex, varIndex)"
              >
                <div
                  v-if="canExpand(variable)"
                  class="arrow-container"
                  :class="{
                    expanded: expandedVariables[getVariableKey(frameIndex, varIndex)],
                  }"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M17.7364 9.3636C17.3849 9.01213 16.8151 9.01213 16.4636 9.36361L12 13.8272L7.53639 9.3636C7.18492 9.01213 6.61507 9.01213 6.2636 9.36361C5.91213 9.71508 5.91213 10.2849 6.2636 10.6364L11.3636 15.7364C11.7151 16.0879 12.285 16.0879 12.6364 15.7364L17.7364 10.6364C18.0879 10.2849 18.0879 9.71507 17.7364 9.3636Z"
                      fill="#949DB8"
                    />
                  </svg>
                </div>

                <span v-else class="expand-placeholder"></span>

                <span class="variable-name">{{ variable.name || 'unnamed' }}</span>
                <span class="variable-display">{{ getVariableDisplay(variable) }}</span>
              </div>

              <div
                v-if="expandedVariables[getVariableKey(frameIndex, varIndex)]"
                class="variable-nested"
              >
                <NestedVariable
                  v-for="(field, fieldIndex) in getVariableFields(variable)"
                  :key="`${frameIndex}_${varIndex}_${fieldIndex}_${field.name || fieldIndex}`"
                  :field="field"
                  :values="safeValues"
                  :visited="createVisitedSet(variable.value_id)"
                  :depth="1"
                  :highlights="safeHighlights"
                  :frame-index="frameIndex"
                  :path="[{ name: variable.name || 'unnamed' }]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NestedVariable from './NestedVariable.vue';
import highlightMixin from './highlightMixin.js';
import {
  isExpandableValue,
  hasResolvableNestedValues,
  formatValueDisplay,
  getNestedFields,
} from './stackTraceValueHelpers.js';

export default {
  name: 'StackTraceViewer',
  components: {
    NestedVariable,
  },
  mixins: [highlightMixin],
  props: {
    severity: {
      type: String,
      default: null,
    },
    stackTraceIndex: {
      type: Number,
      required: true,
    },
    stackTracesCount: {
      type: Number,
      required: true,
    },
    stackTraceItem: {
      type: Object,
      required: true,
      validator(value) {
        return (
          value &&
          typeof value.threadName === 'string' &&
          Array.isArray(value.frames) &&
          Array.isArray(value.values)
        );
      },
    },
  },
  data() {
    return {
      expandedFrames: {},
      expandedVariables: {},
    };
  },

  computed: {
    safeFrames() {
      return Array.isArray(this.stackTraceItem.frames) ? this.stackTraceItem.frames : [];
    },

    safeValues() {
      return Array.isArray(this.stackTraceItem.values) ? this.stackTraceItem.values : [];
    },

    safeHighlights() {
      return Array.isArray(this.stackTraceItem.highlights) ? this.stackTraceItem.highlights : [];
    },
  },

  watch: {
    stackTraceItem: {
      handler() {
        this.expandHighlightedItems();
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.expandHighlightedItems();
  },

  methods: {
    expandHighlightedItems() {
      if (!this.safeHighlights.length) return;

      this.safeFrames.forEach((frame, frameIndex) => {
        if (this.shouldFrameExpand(frameIndex) && this.hasVariables(frame)) {
          this.$set(this.expandedFrames, frameIndex, true);
        }

        if (frame && frame.variables) {
          frame.variables.forEach((variable, varIndex) => {
            if (variable && this.shouldTopLevelVariableExpand(frameIndex, variable.name)) {
              const key = this.getVariableKey(frameIndex, varIndex);
              this.$set(this.expandedVariables, key, true);
            }
          });
        }
      });
    },

    toggleFrame(frame, frameIndex) {
      const canExpand = frame.variables && this.hasVariables(frame);

      if (canExpand) {
        this.$set(this.expandedFrames, frameIndex, !this.expandedFrames[frameIndex]);
      }
    },

    toggleVariable(frameIndex, varIndex) {
      const key = this.getVariableKey(frameIndex, varIndex);
      this.$set(this.expandedVariables, key, !this.expandedVariables[key]);
    },

    getVariableKey(frameIndex, varIndex) {
      return `${frameIndex}_${varIndex}`;
    },

    createVisitedSet(initialValueId) {
      const visited = new Set();
      if (initialValueId) {
        visited.add(initialValueId);
      }
      return visited;
    },

    canExpand(variable) {
      if (!variable || !variable.value_id) return false;
      const value = this.safeValues.find((v) => v && v.id === variable.value_id);
      return hasResolvableNestedValues(value, this.safeValues);
    },

    getVariableDisplay(variable) {
      if (!variable || !variable.value_id) return '(Unknown) null';

      const value = this.safeValues.find((v) => v && v.id === variable.value_id);
      if (!value) return '(Unknown) null';

      return formatValueDisplay(value, variable.class_name);
    },

    hasVariables(frame) {
      return (
        frame && frame.variables && Array.isArray(frame.variables) && frame.variables.length > 0
      );
    },

    getFrameVariables(frame) {
      return this.hasVariables(frame) ? frame.variables : [];
    },

    getFrameDisplayInfo(frame) {
      if (!frame || !frame.element) return 'Unknown frame';

      const element = frame.element;
      let info = `${element.className || 'Unknown'}.${element.methodName || 'unknown'}`;

      if (element.fileName) {
        info += ` (${element.fileName}`;
        if (element.lineNumber) {
          info += `:${element.lineNumber}`;
        }
        info += ')';
      }

      return info;
    },

    getVariableFields(variable) {
      if (!this.canExpand(variable)) return [];

      const value = this.safeValues.find((v) => v && v.id === variable.value_id);
      if (!isExpandableValue(value)) return [];

      return getNestedFields(value);
    },

    getFrameTitle(frame) {
      let title = `${frame.element.className}.${frame.element.methodName}`;
      if (frame.element.fileName) {
        title += ` (${frame.element.fileName}`;
        if (frame.element.lineNumber) {
          title += `:${frame.element.lineNumber}`;
        }
        title += ')';
      }
      return title;
    },

    colorOfVulnerabilityForLine(severity) {
      if (severity === 'low') {
        return 'line-green';
      }

      if (severity === 'medium') {
        return 'line-yellow';
      }

      if (severity === 'high') {
        return 'line-red';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/fonts';
@import '../../assets/css/variables';
@import url('https://fonts.googleapis.com/css2?family=Inconsolata&display=swap');

.stack-trace-viewer {
  font-family: 'Inconsolata', monospace;
  font-size: 14px;
  line-height: 26px;
  color: #021631;
}

.stack-trace-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  @include Aspekta-font(400, 14px, 20px, #5c6a7a);
}

.thread-name {
  font-weight: 500;
  color: #021631;
}

.arrow-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
  cursor: pointer;

  &.expanded {
    transform: rotate(-180deg);
  }
}

.stack-trace-frames {
  position: relative;
  padding-left: 4px;
  margin-top: 16px;
  background-color: #f8fafc;
  max-width: 100%;
  overflow-x: auto;
}

.stack-trace-frames-inner {
  min-width: 100%;
  width: max-content;
}

.frame-item {
  width: 100%;
}

.line {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: lightgrey;
  z-index: 1;
}

.line-green {
  background-color: $low-vulnerability-color;
}

.line-yellow {
  background-color: $medium-vulnerability-color;
}

.line-red {
  background-color: $high-vulnerability-color;
}

.frame-header {
  width: 100%;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: flex-start;

  &::before {
    position: absolute;
    inset: 0;
    background-color: transparent;
    content: '';
    pointer-events: none;
  }

  &:hover {
    &::before {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  &.highlighted {
    background-color: #feeae7;
  }
}

.frame-number {
  margin-left: 40px;
  margin-right: 8px;
  font-family: monospace;
}

.file-info {
  color: #666;
  font-style: italic;
}

.variable-content {
  position: relative;
  cursor: pointer;
  padding-left: 108px;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:hover {
    &::before {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  &.highlighted {
    background-color: #feeae7;
  }
}

.expand-placeholder {
  width: 24px;
  display: inline-block;
}

.variable-name {
  color: #0066cc;
  margin-right: 8px;
  font-weight: 500;
}

.variable-display {
  color: #333;
}

.variable-nested {
  padding-left: 148px;
  border-left: 1px dotted #ccc;
}

.nested-arrow {
  cursor: pointer;
  margin-right: 4px;
}

.nested-name {
  color: #0066cc;
  margin-right: 8px;
  font-weight: 500;
}

.nested-display {
  color: #333;
}

.nested-children {
  margin-left: 20px;
  border-left: 1px dotted #ccc;
  padding-left: 8px;
}
</style>
